//--------------------------------------------------------------------------------------------------Imports
import { Button, Checkbox, Col, Input, Row, Typography } from "antd";
import { RequestHireMegamovil, ResponsePackageItem } from "../../../types/common";
import { StyleSheet } from "../../../StyleSheet";
import { useCallback, useEffect, useState } from "react";
import MarkdownPreviewer from "../../../common/MarkdownPreviewer";
import { useDispatch, useSelector } from "react-redux";
import { getGeneralData } from "../../../stores/slices/generalDataSlice";
import { hireMegaMovil } from "../../../methods/Methods";
import { AppDispatch } from "../../../stores/store";
import { clearAlert, setAlert, showAlert } from "../../../stores/slices/alertSlice";
import Loading from "../../../components/Loading";
import { clearTrackingMegamovilData, fillMegaMovilTrackingSlice } from '../../../stores/slices/megamovilTrackingSlice';
import { getCurpValidationError } from "../../../common/functions";
import QuantitySelector from "./QuantitySelector";
import { clearAlertConfirmation, setAlertConfirmation, showAlertConfirmation } from "../../../stores/slices/alertConfirmationSlice";

const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- Interface
interface HireMegaMovilServicesComponentProps {
    packageSrv: ResponsePackageItem;
};

//-------------------------------------------------------------------------------------------------- Component
const HireMegaMovilServicesComponent = (props: HireMegaMovilServicesComponentProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { selectedContract } = useSelector(getGeneralData);
    // Control
    const [state, setState] = useState<number>(0);
    // Data
    const [clientId, setClientId] = useState<string>("");
    const { packageSrv } = props;
    const [checked, setChecked] = useState<boolean>(false);
    const [loadingHireMegamovil, setLoadingHireMegamovil] = useState<boolean>(false);
    const [curp, setCurp] = useState('');
    const [curpError, setCurpError] = useState('');
    const max = packageSrv.clasificacionMega === "RESIDENCIAL" ? 10 : 50;
    const [cantidad, setCantidad] = useState("");

    const handleFinalizeAlert = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert());
        dispatch(fillMegaMovilTrackingSlice({ clientId }));
    }, [dispatch, clientId]);

    const handleCurpChange = (text: string) => {
        const upperCaseText = text.toUpperCase();
        setCurp(upperCaseText);

        setCurpError(getCurpValidationError(upperCaseText));
    };

    const sendMegaMovilConfirm = useCallback(async () => {
        dispatch(showAlertConfirmation(false));
        dispatch(clearAlertConfirmation());

        let message = "";
        let show = false;
        let typeAlert: "INFO" | "SUCCESS" | "ERROR" = "INFO";


        const packageMegaM: RequestHireMegamovil = {
            id_paquete: packageSrv.idPackage,
            curp: curp,
            cantidad: cantidad
        }
        setLoadingHireMegamovil(true);
        await hireMegaMovil(packageMegaM, clientId)
            .then((response) => {
                const resp = response.data.data[0];
                if (resp) {
                    if (resp?.status === true) {
                        message = resp.message;
                        typeAlert = "SUCCESS";
                        setState(2);
                        dispatch(clearTrackingMegamovilData());
                    } else if (resp?.status === false) {
                        if (resp.error === "3" || resp.error === "4") {
                            typeAlert = "INFO";

                        } else {
                            typeAlert = "ERROR";
                        }
                        message = resp.message;
                        setState(0);
                    }
                    show = true;
                    setLoadingHireMegamovil(false);
                }
            })
            .catch(() => {
                typeAlert = "ERROR";
                message = "Ha ocurrido un error, intentelo más tarde.";
                show = true;
                setState(0);
                setLoadingHireMegamovil(false);
            });

        dispatch(setAlert({
            show: show,
            title: "¡Estimado Usuario!",
            message: message,
            type: typeAlert,
            onFinalize: handleFinalizeAlert
        }));

    }, [packageSrv.idPackage, clientId, dispatch, handleFinalizeAlert, curp, cantidad]);

    const onPressBtnSecondary = useCallback(() => {
        dispatch(showAlertConfirmation(false));
        dispatch(clearAlertConfirmation());
    }, [dispatch]);

    const handleConfirmPurchase = useCallback(() => {
        const validationError = getCurpValidationError(curp);
        const MIN_QUANTITY_TO_HIRE = 1;

        if (validationError) {
            dispatch(setAlert({
                show: true,
                title: "¡Estimado Usuario!",
                message: validationError,
                type: "INFO",
                onFinalize: handleFinalizeAlert
            }));
            return;
        }

        if (cantidad === "" || parseInt(cantidad, 10) < MIN_QUANTITY_TO_HIRE) {
            dispatch(setAlert({
                show: true,
                title: "¡Estimado Usuario!",
                message: `Debes ingresar una cantidad válida, la mínima es ${MIN_QUANTITY_TO_HIRE}.`,
                type: "INFO",
                onFinalize: handleFinalizeAlert
            }));
            return;
        }

        dispatch(setAlertConfirmation({
            onPressBtnPrimary: sendMegaMovilConfirm,
            onPressBtnSecondary: onPressBtnSecondary,
            title: "Confirmación de Contratación",
            message: `¿Está seguro de contratar`,
            subText: `${cantidad} ${parseInt(cantidad) > 1 ? 'líneas' : 'línea'}`,
            subMessage: "Mega móvil",
            show: true
        }));
    }, [cantidad, curp, dispatch, handleFinalizeAlert, onPressBtnSecondary, sendMegaMovilConfirm]);


    useEffect(() => {
        if (selectedContract) {
            setClientId(selectedContract.sucursal + selectedContract.suscriptor);
        }
    }, [selectedContract]);

    const renderStateZero = () => (
        <Row>
            <Col span={18} style={styles.mdContainer}>
                <MarkdownPreviewer
                    h1Style={{
                        fontSize: 15,
                        color: "#000000",
                        fontWeight: 700
                    }}
                    h2Style={{
                        fontSize: 12,
                        color: "#707070",
                        fontWeight: 500
                    }}
                    h3Style={{
                        fontSize: 12,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                        fontWeight: 500
                    }}
                    h4Style={{
                        fontSize: 12,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                        fontWeight: 500
                    }}
                    textStyle={{
                        fontSize: 10,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                    }}
                    linkStyle={{
                        fontSize: 12,
                        color: "#000000",
                        textDecoration: "underline",
                    }}
                    mdSource={packageSrv.onHireButton.mdContent}
                />
            </Col>
            <Col span={6} style={styles.buttonContainer}>
                <Button style={styles.buttonStyle} onClick={() => setState(1)}>{packageSrv.onHireButton.buttonTitle}</Button>
            </Col>
        </Row>
    );

    const renderStateOne = () => (
        <Row style={{ justifyContent: "center" }}>
            {(packageSrv.onConfirmButton.termsAndConditions) && (
                <>
                    <MarkdownPreviewer
                        h1Style={{
                            fontSize: 15,
                            color: "#000000",
                            fontWeight: 500
                        }}
                        h2Style={{
                            fontSize: 12,
                            color: "#707070",
                            fontWeight: 500
                        }}
                        h3Style={{
                            fontSize: 12,
                            color: "#000000",
                            margin: 0,
                            padding: 0,
                            fontWeight: 500
                        }}
                        h4Style={{
                            fontSize: 12,
                            color: "#000000",
                            margin: 0,
                            padding: 0,
                            fontWeight: 500
                        }}
                        textStyle={{
                            fontSize: 12,
                            color: "#000000",
                            margin: 0,
                            padding: 0,
                        }}
                        linkStyle={{
                            fontSize: 12,
                            color: "#000000",
                            textDecoration: "underline",
                        }}
                        mdSource={packageSrv.onConfirmButton.mdContent}
                    />
                    {
                        loadingHireMegamovil ? (
                            <Loading />
                        ) :
                            (
                                <>
                                    <Col span={20} style={{ marginTop: 15, marginBottom: 15 }}>
                                        <Text style={styles.inputTitle}>CURP</Text>
                                        <Input
                                            value={curp}
                                            style={styles.inputText}
                                            onChange={(e) => handleCurpChange(e.target.value)}
                                            maxLength={18}
                                            status={curpError ? "error" : ""}
                                            autoCapitalize="characters"
                                        />
                                        {<Text type="danger" style={styles.emptyFieldError}>{curpError ? curpError : null}</Text>}
                                    </Col>
                                    <Col span={20} style={{ marginTop: 15, marginBottom: 15 }}>
                                        <Text style={styles.inputTitle}>Cantidad de líneas</Text>
                                        <QuantitySelector
                                            quantity={cantidad}
                                            setQuantity={setCantidad}
                                            max={max}
                                        />
                                    </Col>
                                    <Col span={18} style={styles.mdContainer}>
                                        <Checkbox onChange={() => setChecked(!checked)} checked={checked}>
                                            <p style={{
                                                fontSize: 12,
                                                color: "#000000",
                                                padding: 0,
                                                margin: "10px 0px 10px 0px",
                                            }}>
                                                Aceptar Términos y condiciones
                                            </p>
                                        </Checkbox>
                                    </Col>
                                    <Col span={6} style={styles.buttonContainer}>
                                        <Row>
                                            <Col span={24}>
                                                <Button
                                                    style={checked ? styles.buttonStyle : styles.buttonCancelStyle}
                                                    onClick={handleConfirmPurchase}
                                                    disabled={!checked}
                                                >
                                                    {packageSrv.onConfirmButton.buttonTitle}
                                                </Button>
                                            </Col>
                                            <Col span={24}>
                                                <Button
                                                    style={{ ...styles.buttonCancelStyle, backgroundColor: '#fff', color: '#0055c2', border: '1px solid #0055c2', }}
                                                    onClick={() => setState(0)}
                                                >
                                                    Cancelar
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Col>
                                </>
                            )
                    }
                </>
            )}
        </Row>
    );

    return (
        (state === 0) ? (
            renderStateZero()
        ) : ((state === 1) ? (
            renderStateOne()
        ) : null
        ));
};

const styles = StyleSheet.create({
    mdContainer: {
        paddingRight: 10,
    },
    buttonContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    buttonStyle: {
        height: 30,
        fontSize: 10,
        fontWeight: 600,
        padding: "0px 10px 0px 10px",
        borderRadius: 20,
        border: "none",
        marginBottom: 10,
        marginTop: 10,
        color: "#ffffff",
        backgroundColor: "#0055c2",
    },
    buttonCancelStyle: {
        height: 30,
        fontSize: 10,
        fontWeight: 600,
        padding: "0px 10px 0px 10px",
        borderRadius: 20,
        border: "none",
        marginBottom: 10,
        marginTop: 10,
        color: "#ffffff",
        backgroundColor: "gray",
    },
    emptyFieldError: {
        fontSize: 11
    },
    inputTitle: {
        color: "#0055c2",
        fontWeight: 600,
        fontSize: 12
    },
    inputText: {
        marginTop: 15,
        color: "#000000",
        borderColor: '#000000',
        fontSize: 12
    },
});

//-------------------------------------------------------------------------------------------------- Exports
export default HireMegaMovilServicesComponent;
